import { Question, QuestionCore, ModuleId } from '../types'
import { MODULES } from './modules'
import { getLevelById, getLevelTier } from '../config/levels'
import { getQuestionTypeConfig } from '../config/questionTypeRegistry'
import {
  deriveMigrationFields,
  getDifficultyFromTier,
} from '../utils/difficultyTierMapping'

/**
 * Question Loader v2.1
 *
 * ============================================================
 * 🔄 MIGRATION STATUS: BACKWARDS-COMPATIBLE BRIDGE
 * ============================================================
 *
 * This loader now auto-derives the new v2.0 schema fields
 * (difficultyTier, levelCode, group) from the existing `difficulty` field.
 *
 * Priority order for each field:
 * 1. Explicit value from JSON (when JSONs are migrated)
 * 2. Auto-derived value from `difficulty` field (current fallback)
 *
 * Fields NOT derivable (must be added manually during JSON migration):
 * - conceptId
 * - variantIndex
 *
 * See: src/docs/FAMILY_ID_MIGRATION.md for full specification
 * ============================================================
 */
const LEGACY_ID_MAP: Record<number, number> = {
  1: 103, // M6
  2: 104, // M6+
  3: 105, // E6
  4: 201, // B7
  5: 202, // B7+
}

/** Migrate a legacy difficulty ID to the new scheme. Returns the ID unchanged if not legacy. */
export function migrateLevelId(id: number): number {
  return LEGACY_ID_MAP[id] ?? id
}

// Explicit imports for all question JSON files
// Getalbegrip
import getalbegripB6 from './questions/modules/getalbegrip/levels/b6.json'
import getalbegripB6plus from './questions/modules/getalbegrip/levels/b6plus.json'
import getalbegripM6 from './questions/modules/getalbegrip/levels/m6.json'
import getalbegripM6plus from './questions/modules/getalbegrip/levels/m6plus.json'
import getalbegripE6 from './questions/modules/getalbegrip/levels/e6.json'
import getalbegripE6plus from './questions/modules/getalbegrip/levels/e6plus.json'
import getalbegripB7 from './questions/modules/getalbegrip/levels/b7.json'
import getalbegripB7plus from './questions/modules/getalbegrip/levels/b7plus.json'
import getalbegripM7 from './questions/modules/getalbegrip/levels/m7.json'
import getalbegripM7plus from './questions/modules/getalbegrip/levels/m7plus.json'
import getalbegripE7 from './questions/modules/getalbegrip/levels/e7.json'
import getalbegripE7plus from './questions/modules/getalbegrip/levels/e7plus.json'
import getalbegripB8 from './questions/modules/getalbegrip/levels/b8.json'
import getalbegripB8plus from './questions/modules/getalbegrip/levels/b8plus.json'
import getalbegripM8 from './questions/modules/getalbegrip/levels/m8.json'
import getalbegripM8plus from './questions/modules/getalbegrip/levels/m8plus.json'
import getalbegripE8 from './questions/modules/getalbegrip/levels/e8.json'
import getalbegripE8plus from './questions/modules/getalbegrip/levels/e8plus.json'

// Metriek
import metriekB6 from './questions/modules/metriek/levels/b6.json'
import metriekB6plus from './questions/modules/metriek/levels/b6plus.json'
import metriekM6 from './questions/modules/metriek/levels/m6.json'
import metriekM6plus from './questions/modules/metriek/levels/m6plus.json'
import metriekE6 from './questions/modules/metriek/levels/e6.json'
import metriekE6plus from './questions/modules/metriek/levels/e6plus.json'
import metriekB7 from './questions/modules/metriek/levels/b7.json'
import metriekB7plus from './questions/modules/metriek/levels/b7plus.json'
import metriekM7 from './questions/modules/metriek/levels/m7.json'
import metriekM7plus from './questions/modules/metriek/levels/m7plus.json'
import metriekE7 from './questions/modules/metriek/levels/e7.json'
import metriekE7plus from './questions/modules/metriek/levels/e7plus.json'
import metriekB8 from './questions/modules/metriek/levels/b8.json'
import metriekB8plus from './questions/modules/metriek/levels/b8plus.json'
import metriekM8 from './questions/modules/metriek/levels/m8.json'
import metriekM8plus from './questions/modules/metriek/levels/m8plus.json'
import metriekE8 from './questions/modules/metriek/levels/e8.json'
import metriekE8plus from './questions/modules/metriek/levels/e8plus.json'

// TijdGeld
import tijdgeldB6 from './questions/modules/tijdgeld/levels/b6.json'
import tijdgeldB6plus from './questions/modules/tijdgeld/levels/b6plus.json'
import tijdgeldM6 from './questions/modules/tijdgeld/levels/m6.json'
import tijdgeldM6plus from './questions/modules/tijdgeld/levels/m6plus.json'
import tijdgeldE6 from './questions/modules/tijdgeld/levels/e6.json'
import tijdgeldE6plus from './questions/modules/tijdgeld/levels/e6plus.json'
import tijdgeldB7 from './questions/modules/tijdgeld/levels/b7.json'
import tijdgeldB7plus from './questions/modules/tijdgeld/levels/b7plus.json'
import tijdgeldM7 from './questions/modules/tijdgeld/levels/m7.json'
import tijdgeldM7plus from './questions/modules/tijdgeld/levels/m7plus.json'
import tijdgeldE7 from './questions/modules/tijdgeld/levels/e7.json'
import tijdgeldE7plus from './questions/modules/tijdgeld/levels/e7plus.json'
import tijdgeldB8 from './questions/modules/tijdgeld/levels/b8.json'
import tijdgeldB8plus from './questions/modules/tijdgeld/levels/b8plus.json'
import tijdgeldM8 from './questions/modules/tijdgeld/levels/m8.json'
import tijdgeldM8plus from './questions/modules/tijdgeld/levels/m8plus.json'
import tijdgeldE8 from './questions/modules/tijdgeld/levels/e8.json'
import tijdgeldE8plus from './questions/modules/tijdgeld/levels/e8plus.json'

// SpellingWW
import spellingwwB6 from './questions/modules/spellingww/levels/b6.json'
import spellingwwB6plus from './questions/modules/spellingww/levels/b6plus.json'
import spellingwwM6 from './questions/modules/spellingww/levels/m6.json'
import spellingwwM6plus from './questions/modules/spellingww/levels/m6plus.json'
import spellingwwE6 from './questions/modules/spellingww/levels/e6.json'
import spellingwwE6plus from './questions/modules/spellingww/levels/e6plus.json'
import spellingwwB7 from './questions/modules/spellingww/levels/b7.json'
import spellingwwB7plus from './questions/modules/spellingww/levels/b7plus.json'
import spellingwwM7 from './questions/modules/spellingww/levels/m7.json'
import spellingwwM7plus from './questions/modules/spellingww/levels/m7plus.json'
import spellingwwE7 from './questions/modules/spellingww/levels/e7.json'
import spellingwwE7plus from './questions/modules/spellingww/levels/e7plus.json'
import spellingwwB8 from './questions/modules/spellingww/levels/b8.json'
import spellingwwB8plus from './questions/modules/spellingww/levels/b8plus.json'
import spellingwwM8 from './questions/modules/spellingww/levels/m8.json'
import spellingwwM8plus from './questions/modules/spellingww/levels/m8plus.json'
import spellingwwE8 from './questions/modules/spellingww/levels/e8.json'
import spellingwwE8plus from './questions/modules/spellingww/levels/e8plus.json'

// SpellingRegel
import spellingregelB6 from './questions/modules/spellingregel/levels/b6.json'
import spellingregelB6plus from './questions/modules/spellingregel/levels/b6plus.json'
import spellingregelM6 from './questions/modules/spellingregel/levels/m6.json'
import spellingregelM6plus from './questions/modules/spellingregel/levels/m6plus.json'
import spellingregelE6 from './questions/modules/spellingregel/levels/e6.json'
import spellingregelE6plus from './questions/modules/spellingregel/levels/e6plus.json'
import spellingregelB7 from './questions/modules/spellingregel/levels/b7.json'
import spellingregelB7plus from './questions/modules/spellingregel/levels/b7plus.json'
import spellingregelM7 from './questions/modules/spellingregel/levels/m7.json'
import spellingregelM7plus from './questions/modules/spellingregel/levels/m7plus.json'
import spellingregelE7 from './questions/modules/spellingregel/levels/e7.json'
import spellingregelE7plus from './questions/modules/spellingregel/levels/e7plus.json'
import spellingregelB8 from './questions/modules/spellingregel/levels/b8.json'
import spellingregelB8plus from './questions/modules/spellingregel/levels/b8plus.json'
import spellingregelM8 from './questions/modules/spellingregel/levels/m8.json'
import spellingregelM8plus from './questions/modules/spellingregel/levels/m8plus.json'
import spellingregelE8 from './questions/modules/spellingregel/levels/e8.json'
import spellingregelE8plus from './questions/modules/spellingregel/levels/e8plus.json'

// Taalbegrip
import taalbegripB6 from './questions/modules/taalbegrip/levels/b6.json'
import taalbegripB6plus from './questions/modules/taalbegrip/levels/b6plus.json'
import taalbegripM6 from './questions/modules/taalbegrip/levels/m6.json'
import taalbegripM6plus from './questions/modules/taalbegrip/levels/m6plus.json'
import taalbegripE6 from './questions/modules/taalbegrip/levels/e6.json'
import taalbegripE6plus from './questions/modules/taalbegrip/levels/e6plus.json'
import taalbegripB7 from './questions/modules/taalbegrip/levels/b7.json'
import taalbegripB7plus from './questions/modules/taalbegrip/levels/b7plus.json'
import taalbegripM7 from './questions/modules/taalbegrip/levels/m7.json'
import taalbegripM7plus from './questions/modules/taalbegrip/levels/m7plus.json'
import taalbegripE7 from './questions/modules/taalbegrip/levels/e7.json'
import taalbegripE7plus from './questions/modules/taalbegrip/levels/e7plus.json'
import taalbegripB8 from './questions/modules/taalbegrip/levels/b8.json'
import taalbegripB8plus from './questions/modules/taalbegrip/levels/b8plus.json'
import taalbegripM8 from './questions/modules/taalbegrip/levels/m8.json'
import taalbegripM8plus from './questions/modules/taalbegrip/levels/m8plus.json'
import taalbegripE8 from './questions/modules/taalbegrip/levels/e8.json'
import taalbegripE8plus from './questions/modules/taalbegrip/levels/e8plus.json'

// Studievaardig
import studievaardigB6 from './questions/modules/studievaardig/levels/b6.json'
import studievaardigB6plus from './questions/modules/studievaardig/levels/b6plus.json'
import studievaardigM6 from './questions/modules/studievaardig/levels/m6.json'
import studievaardigM6plus from './questions/modules/studievaardig/levels/m6plus.json'
import studievaardigE6 from './questions/modules/studievaardig/levels/e6.json'
import studievaardigE6plus from './questions/modules/studievaardig/levels/e6plus.json'
import studievaardigB7 from './questions/modules/studievaardig/levels/b7.json'
import studievaardigB7plus from './questions/modules/studievaardig/levels/b7plus.json'
import studievaardigM7 from './questions/modules/studievaardig/levels/m7.json'
import studievaardigM7plus from './questions/modules/studievaardig/levels/m7plus.json'
import studievaardigE7 from './questions/modules/studievaardig/levels/e7.json'
import studievaardigE7plus from './questions/modules/studievaardig/levels/e7plus.json'
import studievaardigB8 from './questions/modules/studievaardig/levels/b8.json'
import studievaardigB8plus from './questions/modules/studievaardig/levels/b8plus.json'
import studievaardigM8 from './questions/modules/studievaardig/levels/m8.json'
import studievaardigM8plus from './questions/modules/studievaardig/levels/m8plus.json'
import studievaardigE8 from './questions/modules/studievaardig/levels/e8.json'
import studievaardigE8plus from './questions/modules/studievaardig/levels/e8plus.json'

// Topo
import topoB6 from './questions/modules/topo/levels/b6.json'
import topoB6plus from './questions/modules/topo/levels/b6plus.json'
import topoM6 from './questions/modules/topo/levels/m6.json'
import topoM6plus from './questions/modules/topo/levels/m6plus.json'
import topoE6 from './questions/modules/topo/levels/e6.json'
import topoE6plus from './questions/modules/topo/levels/e6plus.json'
import topoB7 from './questions/modules/topo/levels/b7.json'
import topoB7plus from './questions/modules/topo/levels/b7plus.json'
import topoM7 from './questions/modules/topo/levels/m7.json'
import topoM7plus from './questions/modules/topo/levels/m7plus.json'
import topoE7 from './questions/modules/topo/levels/e7.json'
import topoE7plus from './questions/modules/topo/levels/e7plus.json'
import topoB8 from './questions/modules/topo/levels/b8.json'
import topoB8plus from './questions/modules/topo/levels/b8plus.json'
import topoM8 from './questions/modules/topo/levels/m8.json'
import topoM8plus from './questions/modules/topo/levels/m8plus.json'
import topoE8 from './questions/modules/topo/levels/e8.json'
import topoE8plus from './questions/modules/topo/levels/e8plus.json'

type QuestionBundle = QuestionCore & {
  explanation?: string
  distractorExplanations?: Record<string, string>
  visualAid?: Question['visualAid']
}

/**
 * 🔄 MIGRATION HELPER: Tag questions with their expected difficulty ID
 * based on which file they were imported from.
 *
 * This is the most robust fallback for questions missing the `difficulty` field.
 * The file path determines the correct difficulty ID unambiguously.
 * Questions that already have a valid `difficulty` field are unaffected —
 * this tag is only used as a fallback during the mapping phase.
 */
function withFileDifficulty(
  questions: any[],
  expectedDifficulty: number,
): QuestionBundle[] {
  return questions.map((q) => ({
    ...q,
    _fileDifficulty: expectedDifficulty,
  })) as QuestionBundle[]
}

// Combine all imported question arrays
// 🔄 MIGRATION: Each import is tagged with its expected difficulty ID.
// This ensures correct difficulty assignment even when JSON files are
// migrated to the new format and may be missing the `difficulty` field.
const QUESTION_BUNDLES: QuestionBundle[] = [
  // Getalbegrip
  ...withFileDifficulty(getalbegripB6, 101),
  ...withFileDifficulty(getalbegripB6plus, 102),
  ...withFileDifficulty(getalbegripM6, 103),
  ...withFileDifficulty(getalbegripM6plus, 104),
  ...withFileDifficulty(getalbegripE6, 105),
  ...withFileDifficulty(getalbegripE6plus, 106),
  ...withFileDifficulty(getalbegripB7, 201),
  ...withFileDifficulty(getalbegripB7plus, 202),
  ...withFileDifficulty(getalbegripM7, 203),
  ...withFileDifficulty(getalbegripM7plus, 204),
  ...withFileDifficulty(getalbegripE7, 205),
  ...withFileDifficulty(getalbegripE7plus, 206),
  ...withFileDifficulty(getalbegripB8, 301),
  ...withFileDifficulty(getalbegripB8plus, 302),
  ...withFileDifficulty(getalbegripM8, 303),
  ...withFileDifficulty(getalbegripM8plus, 304),
  ...withFileDifficulty(getalbegripE8, 305),
  ...withFileDifficulty(getalbegripE8plus, 306),

  // Metriek
  ...withFileDifficulty(metriekB6, 101),
  ...withFileDifficulty(metriekB6plus, 102),
  ...withFileDifficulty(metriekM6, 103),
  ...withFileDifficulty(metriekM6plus, 104),
  ...withFileDifficulty(metriekE6, 105),
  ...withFileDifficulty(metriekE6plus, 106),
  ...withFileDifficulty(metriekB7, 201),
  ...withFileDifficulty(metriekB7plus, 202),
  ...withFileDifficulty(metriekM7, 203),
  ...withFileDifficulty(metriekM7plus, 204),
  ...withFileDifficulty(metriekE7, 205),
  ...withFileDifficulty(metriekE7plus, 206),
  ...withFileDifficulty(metriekB8, 301),
  ...withFileDifficulty(metriekB8plus, 302),
  ...withFileDifficulty(metriekM8, 303),
  ...withFileDifficulty(metriekM8plus, 304),
  ...withFileDifficulty(metriekE8, 305),
  ...withFileDifficulty(metriekE8plus, 306),

  // TijdGeld
  ...withFileDifficulty(tijdgeldB6, 101),
  ...withFileDifficulty(tijdgeldB6plus, 102),
  ...withFileDifficulty(tijdgeldM6, 103),
  ...withFileDifficulty(tijdgeldM6plus, 104),
  ...withFileDifficulty(tijdgeldE6, 105),
  ...withFileDifficulty(tijdgeldE6plus, 106),
  ...withFileDifficulty(tijdgeldB7, 201),
  ...withFileDifficulty(tijdgeldB7plus, 202),
  ...withFileDifficulty(tijdgeldM7, 203),
  ...withFileDifficulty(tijdgeldM7plus, 204),
  ...withFileDifficulty(tijdgeldE7, 205),
  ...withFileDifficulty(tijdgeldE7plus, 206),
  ...withFileDifficulty(tijdgeldB8, 301),
  ...withFileDifficulty(tijdgeldB8plus, 302),
  ...withFileDifficulty(tijdgeldM8, 303),
  ...withFileDifficulty(tijdgeldM8plus, 304),
  ...withFileDifficulty(tijdgeldE8, 305),
  ...withFileDifficulty(tijdgeldE8plus, 306),

  // SpellingWW
  ...withFileDifficulty(spellingwwB6, 101),
  ...withFileDifficulty(spellingwwB6plus, 102),
  ...withFileDifficulty(spellingwwM6, 103),
  ...withFileDifficulty(spellingwwM6plus, 104),
  ...withFileDifficulty(spellingwwE6, 105),
  ...withFileDifficulty(spellingwwE6plus, 106),
  ...withFileDifficulty(spellingwwB7, 201),
  ...withFileDifficulty(spellingwwB7plus, 202),
  ...withFileDifficulty(spellingwwM7, 203),
  ...withFileDifficulty(spellingwwM7plus, 204),
  ...withFileDifficulty(spellingwwE7, 205),
  ...withFileDifficulty(spellingwwE7plus, 206),
  ...withFileDifficulty(spellingwwB8, 301),
  ...withFileDifficulty(spellingwwB8plus, 302),
  ...withFileDifficulty(spellingwwM8, 303),
  ...withFileDifficulty(spellingwwM8plus, 304),
  ...withFileDifficulty(spellingwwE8, 305),
  ...withFileDifficulty(spellingwwE8plus, 306),

  // SpellingRegel
  ...withFileDifficulty(spellingregelB6, 101),
  ...withFileDifficulty(spellingregelB6plus, 102),
  ...withFileDifficulty(spellingregelM6, 103),
  ...withFileDifficulty(spellingregelM6plus, 104),
  ...withFileDifficulty(spellingregelE6, 105),
  ...withFileDifficulty(spellingregelE6plus, 106),
  ...withFileDifficulty(spellingregelB7, 201),
  ...withFileDifficulty(spellingregelB7plus, 202),
  ...withFileDifficulty(spellingregelM7, 203),
  ...withFileDifficulty(spellingregelM7plus, 204),
  ...withFileDifficulty(spellingregelE7, 205),
  ...withFileDifficulty(spellingregelE7plus, 206),
  ...withFileDifficulty(spellingregelB8, 301),
  ...withFileDifficulty(spellingregelB8plus, 302),
  ...withFileDifficulty(spellingregelM8, 303),
  ...withFileDifficulty(spellingregelM8plus, 304),
  ...withFileDifficulty(spellingregelE8, 305),
  ...withFileDifficulty(spellingregelE8plus, 306),

  // Taalbegrip
  ...withFileDifficulty(taalbegripB6, 101),
  ...withFileDifficulty(taalbegripB6plus, 102),
  ...withFileDifficulty(taalbegripM6, 103),
  ...withFileDifficulty(taalbegripM6plus, 104),
  ...withFileDifficulty(taalbegripE6, 105),
  ...withFileDifficulty(taalbegripE6plus, 106),
  ...withFileDifficulty(taalbegripB7, 201),
  ...withFileDifficulty(taalbegripB7plus, 202),
  ...withFileDifficulty(taalbegripM7, 203),
  ...withFileDifficulty(taalbegripM7plus, 204),
  ...withFileDifficulty(taalbegripE7, 205),
  ...withFileDifficulty(taalbegripE7plus, 206),
  ...withFileDifficulty(taalbegripB8, 301),
  ...withFileDifficulty(taalbegripB8plus, 302),
  ...withFileDifficulty(taalbegripM8, 303),
  ...withFileDifficulty(taalbegripM8plus, 304),
  ...withFileDifficulty(taalbegripE8, 305),
  ...withFileDifficulty(taalbegripE8plus, 306),

  // Studievaardig
  ...withFileDifficulty(studievaardigB6, 101),
  ...withFileDifficulty(studievaardigB6plus, 102),
  ...withFileDifficulty(studievaardigM6, 103),
  ...withFileDifficulty(studievaardigM6plus, 104),
  ...withFileDifficulty(studievaardigE6, 105),
  ...withFileDifficulty(studievaardigE6plus, 106),
  ...withFileDifficulty(studievaardigB7, 201),
  ...withFileDifficulty(studievaardigB7plus, 202),
  ...withFileDifficulty(studievaardigM7, 203),
  ...withFileDifficulty(studievaardigM7plus, 204),
  ...withFileDifficulty(studievaardigE7, 205),
  ...withFileDifficulty(studievaardigE7plus, 206),
  ...withFileDifficulty(studievaardigB8, 301),
  ...withFileDifficulty(studievaardigB8plus, 302),
  ...withFileDifficulty(studievaardigM8, 303),
  ...withFileDifficulty(studievaardigM8plus, 304),
  ...withFileDifficulty(studievaardigE8, 305),
  ...withFileDifficulty(studievaardigE8plus, 306),

  // Topo
  ...withFileDifficulty(topoB6, 101),
  ...withFileDifficulty(topoB6plus, 102),
  ...withFileDifficulty(topoM6, 103),
  ...withFileDifficulty(topoM6plus, 104),
  ...withFileDifficulty(topoE6, 105),
  ...withFileDifficulty(topoE6plus, 106),
  ...withFileDifficulty(topoB7, 201),
  ...withFileDifficulty(topoB7plus, 202),
  ...withFileDifficulty(topoM7, 203),
  ...withFileDifficulty(topoM7plus, 204),
  ...withFileDifficulty(topoE7, 205),
  ...withFileDifficulty(topoE7plus, 206),
  ...withFileDifficulty(topoB8, 301),
  ...withFileDifficulty(topoB8plus, 302),
  ...withFileDifficulty(topoM8, 303),
  ...withFileDifficulty(topoM8plus, 304),
  ...withFileDifficulty(topoE8, 305),
  ...withFileDifficulty(topoE8plus, 306),
]

const RAW_QUESTIONS: QuestionCore[] = QUESTION_BUNDLES.map((bundle) => {
  const bundleAny = bundle as any
  const migratedDifficulty =
    bundle.difficulty != null ? migrateLevelId(bundle.difficulty) : undefined

  // 🔄 MIGRATION: Determine the correct difficulty ID.
  // Priority order:
  // 1. Explicit `difficulty` field from JSON (migrated if legacy)
  // 2. File-context difficulty (from withFileDifficulty tag — always correct)
  // 3. Derived from other fields (last resort)
  let finalDifficulty: number
  if (
    migratedDifficulty != null &&
    !isNaN(migratedDifficulty) &&
    migratedDifficulty > 0
  ) {
    // Use explicit difficulty from JSON
    finalDifficulty = migratedDifficulty
  } else if (bundleAny._fileDifficulty != null) {
    // Use file-context difficulty (most reliable fallback)
    finalDifficulty = bundleAny._fileDifficulty
    console.warn(
      `🔄 MIGRATION: Using file-context difficulty ${finalDifficulty} for question ${bundle.id} (missing explicit difficulty field)`,
    )
  } else {
    // Last resort: try to derive from other fields
    finalDifficulty = 0
    console.error(
      `❌ MIGRATION: Cannot determine difficulty for question ${bundle.id} — no difficulty, no file context`,
    )
  }

  // Derive migration fields from the final difficulty
  const derived = deriveMigrationFields(finalDifficulty)

  return {
    id: bundle.id,
    module: bundle.module,
    question: bundle.question,
    questionType: bundle.questionType,
    difficulty: finalDifficulty,
    citoDifficulty: bundle.citoDifficulty,
    citoDiscrimination: bundle.citoDiscrimination,
    skill: bundle.skill,
    subSkill: bundle.subSkill,
    familyId: bundle.familyId,
    context: bundle.context,
    options: bundle.options,
    correctAnswer: bundle.correctAnswer,
    interactiveConfig: bundle.interactiveConfig,
    isAnchor: bundle.isAnchor,

    // 🔄 MIGRATION: New v2.0 schema fields
    // Priority: explicit JSON value > derived from difficulty
    difficultyTier: bundleAny.difficultyTier ?? derived.difficultyTier,
    levelCode: bundleAny.levelCode ?? derived.levelCode,
    group: bundleAny.group ?? derived.group,
    conceptId: bundleAny.conceptId,
    variantIndex: bundleAny.variantIndex,
  }
})

const ALL_EXPLANATIONS: Record<
  string,
  {
    explanation?: string
    distractorExplanations?: Record<string, string>
    visualAid?: Question['visualAid']
  }
> = {}

QUESTION_BUNDLES.forEach((bundle) => {
  if (bundle.explanation || bundle.distractorExplanations || bundle.visualAid) {
    ALL_EXPLANATIONS[bundle.id] = {
      explanation: bundle.explanation,
      distractorExplanations: bundle.distractorExplanations,
      visualAid: bundle.visualAid,
    }
  }
})

/**
 * PHASE 1: Question Type Migration v1.1
 *
 * Normalizes questions to ensure backwards compatibility while preparing for CITO.
 * All questions without explicit questionType are treated as 'multiple_choice'.
 */
export function normalizeQuestion(question: QuestionCore): QuestionCore {
  return {
    ...question,
    questionType: question.questionType || 'multiple_choice',
  }
}

/**
 * CITO Eligibility Check
 *
 * Determines if a question can be used in CITO-style assessments.
 * Currently only 'multiple_choice' questions are eligible.
 *
 * Future: May also check citoDifficulty, citoDiscrimination, and familyId constraints.
 */
export function isCitoEligible(question: QuestionCore): boolean {
  const type = question.questionType || 'multiple_choice'
  const config = getQuestionTypeConfig(type)

  // Check registry config and ensure no context (CITO constraint)
  return config.isCitoEligible && !question.context
}

/**
 * Validation Helper (Phase 1 - warnings only)
 *
 * Checks for potential issues in question structure.
 * Returns warnings but does not block loading.
 */
export function validateQuestion(question: QuestionCore): string[] {
  const warnings: string[] = []

  // Warn if familyId exists without skill
  if (question.familyId && !question.skill) {
    warnings.push(`Question ${question.id} has familyId but no skill defined`)
  }

  // Warn if CITO fields are partially filled
  if (
    question.citoDifficulty !== undefined &&
    question.citoDiscrimination === undefined
  ) {
    warnings.push(
      `Question ${question.id} has citoDifficulty but no citoDiscrimination`,
    )
  }

  // Warn if anchor question lacks CITO metrics
  if (
    question.isAnchor &&
    (question.citoDifficulty === undefined ||
      question.citoDiscrimination === undefined)
  ) {
    warnings.push(
      `Anchor question ${question.id} should have CITO difficulty and discrimination values`,
    )
  }
  return warnings
}

// Combine all questions (normalized, without explanations) - for Cito
export const ALL_QUESTIONS_CORE: QuestionCore[] =
  RAW_QUESTIONS.map(normalizeQuestion)

// NEW: CITO-eligible questions subset
export const CITO_ELIGIBLE_QUESTIONS: QuestionCore[] =
  ALL_QUESTIONS_CORE.filter(isCitoEligible)

/**
 * Get a single question with its explanation
 */
export function getQuestionWithExplanation(
  questionId: string,
): Question | null {
  try {
    const core = ALL_QUESTIONS_CORE.find((q) => q.id === questionId)
    if (!core) {
      console.error(`❌ QUESTION ERROR: Question not found: ${questionId}`)
      return null
    }
    const explanation = ALL_EXPLANATIONS[questionId]
    if (!explanation) {
      console.warn(`⚠️ MISSING EXPLANATION: ${questionId} (${core.module})`)
      // Return question without explanation if not found
      return {
        ...core,
        explanation: 'Uitleg wordt binnenkort toegevoegd.',
        distractorExplanations: {},
      }
    }

    // Validate explanation structure
    if (!explanation.explanation) {
      console.warn(
        `⚠️ INCOMPLETE EXPLANATION: ${questionId} - missing main explanation`,
      )
    }
    if (
      !explanation.distractorExplanations ||
      Object.keys(explanation.distractorExplanations).length === 0
    ) {
      console.warn(
        `⚠️ INCOMPLETE EXPLANATION: ${questionId} - missing distractor explanations`,
      )
    }

    // Validate visual aid if present
    if (explanation.visualAid) {
      if (!explanation.visualAid.type) {
        console.error(`❌ VISUAL AID ERROR: ${questionId} - missing type`)
      }
      if (!explanation.visualAid.data) {
        console.error(`❌ VISUAL AID ERROR: ${questionId} - missing data`)
      }
    }
    return {
      ...core,
      explanation: explanation.explanation,
      distractorExplanations: explanation.distractorExplanations,
      visualAid: explanation.visualAid,
    }
  } catch (error) {
    console.error('❌ Error in getQuestionWithExplanation:', error, questionId)
    return null
  }
}

/**
 * Get all questions for a module (with explanations)
 * Used by: PracticeSession, normal practice mode
 */
export function getQuestionsByModule(moduleId: ModuleId): Question[] {
  try {
    const questions = ALL_QUESTIONS_CORE.filter((q) => q.module === moduleId)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
    if (questions.length === 0) {
      console.warn(`⚠️ No questions found for module: ${moduleId}`)
    }
    return questions
  } catch (error) {
    console.error('❌ Error in getQuestionsByModule:', error, moduleId)
    return []
  }
}

/**
 * UPDATED: Get questions for CITO (with intelligent selection)
 *
 * CITO Selection Algorithm v1.2:
 * - Only CITO-eligible questions (multiple_choice)
 * - Max 1 question per familyId (prevents repeated variants)
 * - Balanced across skills (proportional representation)
 * - Balanced difficulty spread using level tiers (easy/medium/hard)
 * - Randomized within constraints
 * - OPTIONS ARE SHUFFLED to prevent answer position bias
 *
 * Used by: CitoSimulator
 */
export function getCitoQuestions(count: number = 30): QuestionCore[] {
  try {
    // 1. Filter CITO-eligible questions only
    const eligible = CITO_ELIGIBLE_QUESTIONS.filter(isCitoEligible)
    if (eligible.length === 0) {
      console.error('❌ CITO: No eligible questions found')
      return []
    }

    // 2. Group by skill for balanced coverage
    const bySkill = new Map<string, QuestionCore[]>()
    eligible.forEach((q) => {
      const skill = q.skill || 'unknown'
      if (!bySkill.has(skill)) {
        bySkill.set(skill, [])
      }
      bySkill.get(skill)!.push(q)
    })

    // 3. Within each skill, group by familyId and pick max 1 per family
    const selectedBySkill = new Map<string, QuestionCore[]>()
    bySkill.forEach((questions, skill) => {
      const byFamily = new Map<string, QuestionCore[]>()
      questions.forEach((q) => {
        const familyId = q.familyId || q.id // Fallback to question ID if no familyId
        if (!byFamily.has(familyId)) {
          byFamily.set(familyId, [])
        }
        byFamily.get(familyId)!.push(q)
      })

      // Pick one random question from each family
      const skillQuestions: QuestionCore[] = []
      byFamily.forEach((familyQuestions) => {
        const randomIndex = Math.floor(Math.random() * familyQuestions.length)
        skillQuestions.push(familyQuestions[randomIndex])
      })
      selectedBySkill.set(skill, skillQuestions)
    })

    // 4. Calculate questions per skill (proportional to available questions)
    const totalAvailable = Array.from(selectedBySkill.values()).reduce(
      (sum, questions) => sum + questions.length,
      0,
    )
    const questionsPerSkill = new Map<string, number>()
    selectedBySkill.forEach((questions, skill) => {
      const proportion = questions.length / totalAvailable
      const allocated = Math.max(1, Math.round(count * proportion))
      questionsPerSkill.set(skill, allocated)
    })

    // 5. Select questions from each skill with difficulty balance using level tiers
    const selected: QuestionCore[] = []
    selectedBySkill.forEach((questions, skill) => {
      const targetCount = questionsPerSkill.get(skill) || 0

      // Group by difficulty tier (easy/medium/hard) using level config
      const easyQuestions = questions.filter(
        (q) => getLevelTier(q.difficulty) === 'easy',
      )
      const mediumQuestions = questions.filter(
        (q) => getLevelTier(q.difficulty) === 'medium',
      )
      const hardQuestions = questions.filter(
        (q) => getLevelTier(q.difficulty) === 'hard',
      )

      // Aim for difficulty distribution: 40% easy, 40% medium, 20% hard
      const easyCount = Math.round(targetCount * 0.4)
      const mediumCount = Math.round(targetCount * 0.4)
      const hardCount = targetCount - easyCount - mediumCount

      // Shuffle each difficulty tier
      const shuffledEasy = easyQuestions.sort(() => Math.random() - 0.5)
      const shuffledMedium = mediumQuestions.sort(() => Math.random() - 0.5)
      const shuffledHard = hardQuestions.sort(() => Math.random() - 0.5)

      // Take from each difficulty tier
      selected.push(...shuffledEasy.slice(0, easyCount))
      selected.push(...shuffledMedium.slice(0, mediumCount))
      selected.push(...shuffledHard.slice(0, hardCount))

      // If we don't have enough questions at target difficulties, fill from available
      const remaining =
        targetCount -
        (Math.min(easyCount, shuffledEasy.length) +
          Math.min(mediumCount, shuffledMedium.length) +
          Math.min(hardCount, shuffledHard.length))
      if (remaining > 0) {
        const allShuffled = questions
          .filter((q) => !selected.includes(q))
          .sort(() => Math.random() - 0.5)
        selected.push(...allShuffled.slice(0, remaining))
      }
    })

    // 6. Final shuffle and limit to exact count
    const finalSelection = selected
      .sort(() => Math.random() - 0.5)
      .slice(0, count)

    // 7. CRITICAL FIX: Shuffle options for each question to prevent answer position bias
    const shuffledQuestions = finalSelection.map((q) =>
      shuffleQuestionOptions(q),
    )

    // 8. Log selection statistics (development only)
    if (process.env.NODE_ENV === 'development') {
      console.group('🎯 CITO Question Selection')
      console.log(`Requested: ${count} questions`)
      console.log(`Selected: ${shuffledQuestions.length} questions`)

      // Skill distribution
      const skillDist = new Map<string, number>()
      shuffledQuestions.forEach((q) => {
        const skill = q.skill || 'unknown'
        skillDist.set(skill, (skillDist.get(skill) || 0) + 1)
      })
      console.log('Skill distribution:', Object.fromEntries(skillDist))

      // Tier distribution
      const tierDist = { easy: 0, medium: 0, hard: 0 }
      shuffledQuestions.forEach((q) => {
        const tier = getLevelTier(q.difficulty)
        tierDist[tier]++
      })
      console.log('Tier distribution:', tierDist)

      // Family uniqueness check
      const familyIds = new Set(shuffledQuestions.map((q) => q.familyId))
      console.log(
        `Unique families: ${familyIds.size} (should equal ${shuffledQuestions.length})`,
      )

      // Answer position distribution (verify shuffling works)
      const answerDist = { 0: 0, 1: 0, 2: 0, 3: 0 }
      shuffledQuestions.forEach((q) => {
        const ans = q.correctAnswer as 0 | 1 | 2 | 3
        if (answerDist[ans] !== undefined) answerDist[ans]++
      })
      console.log(
        'Answer position distribution (should be ~equal):',
        answerDist,
      )
      console.groupEnd()
    }
    if (shuffledQuestions.length < count) {
      console.warn(
        `⚠️ CITO: Requested ${count} questions, but only ${shuffledQuestions.length} available after filtering`,
      )
    }
    return shuffledQuestions
  } catch (error) {
    console.error('❌ Error in getCitoQuestions:', error)
    return []
  }
}

/**
 * NEW: Get CITO questions by skill (for targeted practice)
 *
 * Selects questions from a specific skill with same CITO constraints
 * UPDATED: Uses level tier system instead of legacy difficulty 1-5
 */
export function getCitoQuestionsBySkill(
  skill: string,
  count: number = 10,
): QuestionCore[] {
  try {
    const eligible = CITO_ELIGIBLE_QUESTIONS.filter(
      (q) => isCitoEligible(q) && q.skill === skill,
    )
    if (eligible.length === 0) {
      console.warn(`⚠️ CITO: No eligible questions found for skill: ${skill}`)
      return []
    }

    // Group by familyId
    const byFamily = new Map<string, QuestionCore[]>()
    eligible.forEach((q) => {
      const familyId = q.familyId || q.id
      if (!byFamily.has(familyId)) {
        byFamily.set(familyId, [])
      }
      byFamily.get(familyId)!.push(q)
    })

    // Pick one from each family
    const uniqueQuestions: QuestionCore[] = []
    byFamily.forEach((familyQuestions) => {
      const randomIndex = Math.floor(Math.random() * familyQuestions.length)
      uniqueQuestions.push(familyQuestions[randomIndex])
    })

    // Balance difficulty using level tiers (new system)
    const easyQuestions = uniqueQuestions.filter(
      (q) => getLevelTier(q.difficulty) === 'easy',
    )
    const mediumQuestions = uniqueQuestions.filter(
      (q) => getLevelTier(q.difficulty) === 'medium',
    )
    const hardQuestions = uniqueQuestions.filter(
      (q) => getLevelTier(q.difficulty) === 'hard',
    )

    const easyCount = Math.round(count * 0.4)
    const mediumCount = Math.round(count * 0.4)
    const hardCount = count - easyCount - mediumCount

    const selected: QuestionCore[] = []
    selected.push(
      ...easyQuestions.sort(() => Math.random() - 0.5).slice(0, easyCount),
    )
    selected.push(
      ...mediumQuestions.sort(() => Math.random() - 0.5).slice(0, mediumCount),
    )
    selected.push(
      ...hardQuestions.sort(() => Math.random() - 0.5).slice(0, hardCount),
    )

    // Fill remaining if needed
    const remaining = count - selected.length
    if (remaining > 0) {
      const availableQuestions = uniqueQuestions
        .filter((q) => !selected.includes(q))
        .sort(() => Math.random() - 0.5)
      selected.push(...availableQuestions.slice(0, remaining))
    }
    return selected.sort(() => Math.random() - 0.5).slice(0, count)
  } catch (error) {
    console.error('❌ Error in getCitoQuestionsBySkill:', error)
    return []
  }
}

/**
 * NEW: Get skill-based performance summary
 *
 * Analyzes available CITO questions by skill and subSkill
 * UPDATED: Uses level tiers instead of legacy difficulty 1-5
 */
export function getCitoSkillSummary(): {
  totalQuestions: number
  bySkill: Map<
    string,
    {
      total: number
      bySubSkill: Map<string, number>
      byTier: { easy: number; medium: number; hard: number }
    }
  >
} {
  const bySkill = new Map<
    string,
    {
      total: number
      bySubSkill: Map<string, number>
      byTier: { easy: number; medium: number; hard: number }
    }
  >()
  CITO_ELIGIBLE_QUESTIONS.forEach((q) => {
    const skill = q.skill || 'unknown'
    if (!bySkill.has(skill)) {
      bySkill.set(skill, {
        total: 0,
        bySubSkill: new Map(),
        byTier: { easy: 0, medium: 0, hard: 0 },
      })
    }
    const skillData = bySkill.get(skill)!
    skillData.total++
    const tier = getLevelTier(q.difficulty)
    skillData.byTier[tier]++
    if (q.subSkill) {
      const subSkillCount = skillData.bySubSkill.get(q.subSkill) || 0
      skillData.bySubSkill.set(q.subSkill, subSkillCount + 1)
    }
  })
  return {
    totalQuestions: CITO_ELIGIBLE_QUESTIONS.length,
    bySkill,
  }
}

/**
 * Get all questions (with explanations) - backwards compatible
 * Used by: Legacy code that expects full questions
 */
export function getAllQuestions(): Question[] {
  try {
    const questions = ALL_QUESTIONS_CORE.map((q) =>
      getQuestionWithExplanation(q.id),
    ).filter((q): q is Question => q !== null)

    // Log statistics
    const totalQuestions = ALL_QUESTIONS_CORE.length
    const questionsWithExplanations = questions.filter(
      (q) =>
        q.explanation &&
        q.explanation !== 'Uitleg wordt binnenkort toegevoegd.',
    ).length
    const missingExplanations = totalQuestions - questionsWithExplanations
    console.log(`📊 Question Statistics:`)
    console.log(`   Total questions: ${totalQuestions}`)
    console.log(`   With explanations: ${questionsWithExplanations}`)
    console.log(`   Missing explanations: ${missingExplanations}`)
    if (missingExplanations > 0) {
      console.warn(
        `⚠️ ${missingExplanations} questions are missing explanations`,
      )
    }
    return questions
  } catch (error) {
    console.error('❌ Error in getAllQuestions:', error)
    return []
  }
}

/**
 * NIEUW: Get questions by type (for special rendering)
 * Used by: PracticeSession to render special question types
 */
export function getQuestionsByType(type: string): Question[] {
  try {
    return ALL_QUESTIONS_CORE.filter((q) => q.questionType === type)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    console.error('❌ Error in getQuestionsByType:', error)
    return []
  }
}

/**
 * NIEUW: Validate all questions and explanations
 * Used by: Development to check data integrity
 */
export function validateQuestionsAndExplanations(): {
  valid: boolean
  errors: string[]
  warnings: string[]
} {
  const errors: string[] = []
  const warnings: string[] = []
  ALL_QUESTIONS_CORE.forEach((question) => {
    // Validate question structure
    if (!question.id) {
      errors.push(`Question missing ID`)
    }
    if (!question.module) {
      errors.push(`Question ${question.id} missing module`)
    }
    if (!question.question) {
      errors.push(`Question ${question.id} missing question text`)
    }
    if (!question.options || question.options.length === 0) {
      errors.push(`Question ${question.id} missing options`)
    }
    if (
      question.correctAnswer === undefined ||
      question.correctAnswer === null
    ) {
      errors.push(`Question ${question.id} missing correctAnswer`)
    }

    // Check for explanation
    const explanation = ALL_EXPLANATIONS[question.id]
    if (!explanation) {
      warnings.push(`Question ${question.id} missing explanation`)
    } else {
      if (!explanation.explanation) {
        warnings.push(`Question ${question.id} missing main explanation text`)
      }
      if (
        !explanation.distractorExplanations ||
        Object.keys(explanation.distractorExplanations).length === 0
      ) {
        warnings.push(`Question ${question.id} missing distractor explanations`)
      }
    }
  })
  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

// Export modules for convenience
export { MODULES }

// UPDATED: Add validation logging (development only)
if (process.env.NODE_ENV === 'development') {
  const allWarnings = ALL_QUESTIONS_CORE.flatMap(validateQuestion)
  if (allWarnings.length > 0) {
    console.group('📋 Question Validation Warnings (Phase 1)')
    allWarnings.forEach((warning) => console.warn(warning))
    console.groupEnd()
  }
  console.log(
    `✅ Loaded ${ALL_QUESTIONS_CORE.length} questions (${CITO_ELIGIBLE_QUESTIONS.length} CITO-eligible)`,
  )
}

/**
 * NIEUW: Shuffle question options and update correctAnswer accordingly
 * This ensures answer positions are randomized for fair testing
 */
export function shuffleQuestionOptions<T extends QuestionCore>(question: T): T {
  const type = question.questionType || 'multiple_choice'
  const config = getQuestionTypeConfig(type)

  // Don't shuffle if config says no
  if (!config.shouldShuffleOptions) {
    return question
  }

  // Create array of indices and shuffle them
  const indices = question.options.map((_, i) => i)

  // Fisher-Yates shuffle
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[indices[i], indices[j]] = [indices[j], indices[i]]
  }

  // Create new shuffled options array
  const shuffledOptions = indices.map((i) => question.options[i])

  // Find new position of correct answer
  const newCorrectAnswer = indices.indexOf(question.correctAnswer)
  return {
    ...question,
    options: shuffledOptions,
    correctAnswer: newCorrectAnswer,
  } as T
}

/**
 * Shuffle options for a full Question (with explanations)
 * Also remaps distractorExplanations to match new option positions
 */
export function shuffleQuestionWithExplanations(question: Question): Question {
  const type = question.questionType || 'multiple_choice'
  const config = getQuestionTypeConfig(type)

  // Don't shuffle if config says no
  if (!config.shouldShuffleOptions) {
    return question
  }

  // Create array of indices and shuffle them
  const indices = question.options.map((_, i) => i)

  // Fisher-Yates shuffle
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[indices[i], indices[j]] = [indices[j], indices[i]]
  }

  // Create new shuffled options array
  const shuffledOptions = indices.map((i) => question.options[i])

  // Find new position of correct answer
  const newCorrectAnswer = indices.indexOf(question.correctAnswer)

  // Remap distractor explanations to new indices
  // Original: { "0": "explanation for option 0", "1": "explanation for option 1", ... }
  // After shuffle: need to map old index -> new index
  let remappedDistractorExplanations: Record<string, string> | undefined
  if (question.distractorExplanations) {
    remappedDistractorExplanations = {}

    // For each new position, find what original index is there
    // and copy its explanation to the new position
    indices.forEach((originalIndex, newIndex) => {
      const originalExplanation =
        question.distractorExplanations?.[originalIndex.toString()]
      if (originalExplanation) {
        remappedDistractorExplanations![newIndex.toString()] =
          originalExplanation
      }
    })
  }
  return {
    ...question,
    options: shuffledOptions,
    correctAnswer: newCorrectAnswer,
    distractorExplanations: remappedDistractorExplanations,
  }
}

/**
 * Get questions for a specific module and level
 * This is the primary API for practice sessions.
 * Encapsulates the module + difficulty filtering logic.
 */
export function getQuestionsForLevel(
  moduleId: ModuleId,
  levelId: number,
): Question[] {
  try {
    const level = getLevelById(levelId)
    if (!level) {
      console.warn(`⚠️ Unknown level ID: ${levelId}`)
      return []
    }

    const questions = ALL_QUESTIONS_CORE.filter(
      (q) => q.module === moduleId && q.difficulty === levelId,
    )
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)

    if (questions.length === 0) {
      console.warn(
        `⚠️ No questions found for module ${moduleId} at level ${level.label} (id: ${levelId})`,
      )
    }

    return questions
  } catch (error) {
    console.error('❌ Error in getQuestionsForLevel:', error)
    return []
  }
}

/**
 * Get question count for a specific module and level
 * Useful for UI display without loading full question data
 */
export function getQuestionCountForLevel(
  moduleId: ModuleId,
  levelId: number,
): number {
  return ALL_QUESTIONS_CORE.filter(
    (q) => q.module === moduleId && q.difficulty === levelId,
  ).length
}

/**
 * Validate all questions match their expected level files
 * Used by: Development to check data integrity
 */
export function validateLevelMapping(): {
  valid: boolean
  issues: string[]
} {
  const issues: string[] = []

  ALL_QUESTIONS_CORE.forEach((q) => {
    const level = getLevelById(q.difficulty)
    if (!level) {
      issues.push(
        `Question ${q.id} has difficulty ${q.difficulty} which doesn't match any level config`,
      )
    }
  })

  return {
    valid: issues.length === 0,
    issues,
  }
}
